/**
 * InboundSimulatorTab - Simulate inbound messages from external systems
 *
 * Allows users to test inbound integration flows by sending messages
 * as if they were coming from external systems (ERP, TMS, OMS, etc.)
 *
 * Features:
 * - Select source system (ACME_ERP, etc.)
 * - Select message type (EXPECTED_RECEIPT, ASN, etc.)
 * - Load sample payload template
 * - Edit payload
 * - Send to WMS inbound handler
 * - View result
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Code as CodeIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import IntegrationService from '../../../services/IntegrationService';
import { useTheme as useAppTheme } from '../../../contexts/ThemeContext';

const InboundSimulatorTab = () => {
  const { colors } = useAppTheme();

  // Form state
  const [sourceSystem, setSourceSystem] = useState('');
  const [messageType, setMessageType] = useState('');
  const [payload, setPayload] = useState('');
  const [jsonError, setJsonError] = useState(null);

  // Options
  const [systems, setSystems] = useState([]);
  const [messageTypes, setMessageTypes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Execution state
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Load options on mount
  useEffect(() => {
    loadOptions();
  }, []);

  // Load template when message type changes
  useEffect(() => {
    if (messageType) {
      loadTemplate(messageType);
    }
  }, [messageType]);

  const loadOptions = async () => {
    setLoadingOptions(true);
    try {
      const [systemsData, typesData] = await Promise.all([
        IntegrationService.listInboundSystems(),
        IntegrationService.listInboundMessageTypes(),
      ]);
      setSystems(systemsData || []);
      setMessageTypes(typesData || []);

      // Default to first system if available
      if (systemsData && systemsData.length > 0) {
        setSourceSystem(systemsData[0].systemCode);
      }
    } catch (err) {
      console.error('Failed to load options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadTemplate = async (msgType) => {
    try {
      const template = await IntegrationService.getMessageTemplate(msgType);
      setPayload(JSON.stringify(template, null, 2));
      setJsonError(null);
    } catch (err) {
      console.error('Failed to load template:', err);
    }
  };

  const validateJson = (text) => {
    if (!text.trim()) {
      setJsonError(null);
      return true;
    }
    try {
      JSON.parse(text);
      setJsonError(null);
      return true;
    } catch (e) {
      setJsonError(e.message);
      return false;
    }
  };

  const handlePayloadChange = (e) => {
    const value = e.target.value;
    setPayload(value);
    validateJson(value);
  };

  const handleSend = async () => {
    if (!sourceSystem || !messageType) {
      setResult({
        success: false,
        error: 'Please select a source system and message type',
      });
      return;
    }

    if (!validateJson(payload)) {
      setResult({
        success: false,
        error: 'Invalid JSON payload',
      });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const parsedPayload = payload.trim() ? JSON.parse(payload) : {};
      const response = await IntegrationService.sendInboundMessage(
        sourceSystem,
        messageType,
        parsedPayload
      );

      setResult({
        success: response.success,
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      setResult({
        success: false,
        error: err.message || 'Failed to send message',
      });
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setPayload('');
    setResult(null);
    setJsonError(null);
    if (messageType) {
      loadTemplate(messageType);
    }
  };

  const handleCopyPayload = () => {
    navigator.clipboard.writeText(payload);
  };

  const handleCopyResult = () => {
    if (result?.data) {
      navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
    }
  };

  const formatJson = (obj) => {
    if (!obj) return '';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const getMessageTypeInfo = (code) => {
    return messageTypes.find((mt) => mt.code === code);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', bgcolor: colors.background }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          Inbound Message Simulator
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
          Simulate messages from external systems (ERP, TMS, OMS) to test inbound integration flows
        </Typography>
      </Box>

      {/* Main content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left panel - Configuration */}
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${colors.border}`,
          }}
        >
          {/* Source & Type selectors */}
          <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ color: colors.textSecondary }}>Source System</InputLabel>
              <Select
                value={sourceSystem}
                onChange={(e) => setSourceSystem(e.target.value)}
                label="Source System"
                disabled={loadingOptions}
                sx={{
                  color: colors.textPrimary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                }}
              >
                {systems.map((sys) => (
                  <MenuItem key={sys.systemCode} value={sys.systemCode}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {sys.name || sys.systemCode}
                      <Chip
                        label={sys.systemType}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel sx={{ color: colors.textSecondary }}>Message Type</InputLabel>
              <Select
                value={messageType}
                onChange={(e) => setMessageType(e.target.value)}
                label="Message Type"
                disabled={loadingOptions}
                sx={{
                  color: colors.textPrimary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                }}
              >
                {messageTypes.map((mt) => (
                  <MenuItem key={mt.code} value={mt.code}>
                    <Box>
                      <Typography variant="body2">{mt.name}</Typography>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {mt.domain}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Message type description */}
          {messageType && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Alert
                severity="info"
                icon={<CodeIcon />}
                sx={{
                  bgcolor: `${colors.primary}10`,
                  color: colors.textSecondary,
                  '& .MuiAlert-icon': { color: colors.primary },
                }}
              >
                <Typography variant="body2">
                  <strong>{getMessageTypeInfo(messageType)?.name}</strong>
                  {' - '}
                  {messageType === 'EXPECTED_RECEIPT' &&
                    'Creates a receipt with expected lines. WMS will track receiving against this.'}
                  {messageType === 'ASN' &&
                    'Creates receipt with LPN-level detail. Enables blind receiving.'}
                  {messageType === 'PO_RELEASE' &&
                    'Creates or updates a receipt from a purchase order release.'}
                  {messageType === 'ITEM_MASTER_SYNC' && 'Creates or updates item master data.'}
                  {messageType === 'VENDOR_SYNC' && 'Creates or updates vendor master data.'}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Payload editor */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary }}>
                Payload (JSON)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Copy Payload">
                  <IconButton size="small" onClick={handleCopyPayload} sx={{ color: colors.textSecondary }}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset to Template">
                  <IconButton size="small" onClick={handleReset} sx={{ color: colors.textSecondary }}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <TextField
              multiline
              fullWidth
              value={payload}
              onChange={handlePayloadChange}
              placeholder='{"key": "value"}'
              error={!!jsonError}
              helperText={jsonError}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '100%',
                  alignItems: 'flex-start',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  color: colors.textPrimary,
                  bgcolor: colors.surface,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-inputMultiline': {
                  height: '100% !important',
                  overflow: 'auto !important',
                },
              }}
            />
          </Box>

          {/* Send button */}
          <Box sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending || !sourceSystem || !messageType}
              sx={{
                bgcolor: colors.primary,
                '&:hover': { bgcolor: colors.primaryDark || colors.primary },
                '&.Mui-disabled': { bgcolor: colors.border },
              }}
            >
              {sending ? 'Sending...' : 'Send Message to WMS'}
            </Button>
          </Box>
        </Box>

        {/* Right panel - Results */}
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: colors.surface,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary }}>
              Response
            </Typography>
            {result && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {result.success ? (
                  <Chip
                    icon={<SuccessIcon sx={{ fontSize: 16 }} />}
                    label={`${result.status} ${result.statusText}`}
                    size="small"
                    sx={{
                      bgcolor: `${colors.success || '#4caf50'}20`,
                      color: colors.success || '#4caf50',
                    }}
                  />
                ) : (
                  <Chip
                    icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                    label={result.status ? `${result.status} ${result.statusText}` : 'Error'}
                    size="small"
                    sx={{
                      bgcolor: `${colors.error}20`,
                      color: colors.error,
                    }}
                  />
                )}
                <Tooltip title="Copy Response">
                  <IconButton size="small" onClick={handleCopyResult} sx={{ color: colors.textSecondary }}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {!result ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: colors.textMuted }}>
                  Send a message to see the response
                </Typography>
              </Box>
            ) : result.error && !result.data ? (
              <Alert severity="error" sx={{ bgcolor: `${colors.error}10` }}>
                {result.error}
              </Alert>
            ) : (
              <Box>
                {/* Timestamp */}
                <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 2 }}>
                  Received at: {new Date(result.timestamp).toLocaleString()}
                </Typography>

                {/* Success/Error indicator */}
                {result.success ? (
                  <Alert
                    severity="success"
                    sx={{ mb: 2, bgcolor: `${colors.success || '#4caf50'}10` }}
                  >
                    Message processed successfully
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ mb: 2, bgcolor: `${colors.error}10` }}>
                    Message processing failed
                  </Alert>
                )}

                {/* Response data */}
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1 }}>
                  Response Data:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: colors.background,
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 400,
                    fontSize: '0.8rem',
                    color: colors.textSecondary,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {formatJson(result.data)}
                </Box>

                {/* Created entity info */}
                {result.data?.createdEntityId && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1 }}>
                      Created Entity:
                    </Typography>
                    <Chip
                      label={`ID: ${result.data.createdEntityId}`}
                      sx={{
                        bgcolor: `${colors.success || '#4caf50'}20`,
                        color: colors.success || '#4caf50',
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InboundSimulatorTab;
